import styles from "./CobraCensus.module.css";
import { useEffect, useState } from "react";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import Alert from "uicomponents/Alert";
import CensusDrawer from "uicomponents/CensusDrawer";
import CensusTable from "uicomponents/CensusTable";
import { useDeleteCobraParticipantMutation } from "types/generated/operations";
import { Modal } from "@justworkshr/milo-components";
import { CobraCensusType } from "types";
import { DependentType, EmployeeType } from "uicomponents/CensusTable/types";
import { errorMessageMap } from "utils/helper";

interface ICobraCensusProps {
  data: CobraCensusType;
  refetchData: () => void;
  setNextStep: (submitApp?: boolean) => void;
  setPreviousStep: () => void;
}

interface IDrawerState {
  status: string;
  initialData?: DependentType | EmployeeType;
}

const CobraCensus = (props: ICobraCensusProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(5, "current cobra participant information", 1);
  }, []);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const [deleteCobraParticipant, { loading: deleteCobraParticipantLoading }] =
    useDeleteCobraParticipantMutation();

  const [drawerState, setDrawerState] = useState<IDrawerState>({
    status: "closed",
    initialData: undefined,
  });

  const { data, setNextStep, setPreviousStep } = props;

  const [existingCensuses, setExistingCensuses] = useState(
    data?.cobraCensuses || []
  );

  useEffect(() => {
    if (data?.cobraCensuses) {
      setExistingCensuses(data.cobraCensuses);
    }
  }, [data]);

  const handleSaveAndContinue = async () => {
    setIsLoadingSteps(true);
    trackSaveAndContinueUserStep(5, "Cobra Participants", 1);
    setNextStep(true);
  };

  const handleDelete = async (uuid: string) => {
    try {
      if (!uuid) {
        return true;
      }
      await deleteCobraParticipant({
        variables: {
          uuid,
        },
      });
      props.refetchData();
      return true;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setErrorMessage(errorMessageMap[errorMessage] || "Please try again");
      trackFormError(error, errorMessage);
      setIsError(true);
      return false;
    }
  };

  const openDrawer = (initialData?: DependentType | EmployeeType) => () => {
    setDrawerState({
      status: "animating",
      initialData,
    });
    setTimeout(() => {
      setDrawerState({
        initialData,
        status: "open",
      });
    }, 10);
  };

  const closeDrawer = (refetch?: boolean) => {
    setDrawerState({
      ...drawerState,
      status: "animating",
    });
    setTimeout(() => {
      setDrawerState({
        status: "closed",
      });
    }, 500);
    if (refetch) props.refetchData();
  };

  return (
    <>
      <Modal
        isOpen={drawerState.status !== "closed"}
        onClose={() => closeDrawer()}
      >
        {drawerState.status !== "closed" && (
          <CensusDrawer
            isOpen={drawerState.status === "open"}
            initialData={drawerState.initialData}
            closeDrawer={closeDrawer}
            isCobra
          />
        )}
      </Modal>
      <div>
        <div className={styles.censusHeading}>
          <h3>COBRA participants</h3>
          <div className={styles.description}>
            We also need all former employees and their dependents that are
            currently on COBRA.
          </div>

          <CensusTable
            censusList={existingCensuses}
            handleBackClick={setPreviousStep}
            handleDelete={handleDelete}
            offerHealthInsurance={
              !!props.data?.healthInsuranceInformation
                ?.currentlyOfferHealthInsurance
            }
            isLoading={deleteCobraParticipantLoading || isLoadingSteps}
            openDrawer={openDrawer}
            nextButtonText={isLoadingSteps ? "Loading" : "Save & submit"}
            handleSaveAndContinue={handleSaveAndContinue}
            isCobra
          />

          {isError && <Alert message={errorMessage} />}
        </div>
      </div>
    </>
  );
};

export default CobraCensus;
